import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { buildFieldTag, buildLinkTag } from 'app/utils/template-parser.utils';

@Component({
  selector: 'app-modal-insert-template-field',
  templateUrl: './modal-insert-template-field.component.html',
  styleUrls: ['./modal-insert-template-field.component.scss'],
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
})
export class ModalInsertTemplateFieldComponent implements OnInit {
  dialogRef = inject(MatDialogRef<ModalInsertTemplateFieldComponent>);
  data = inject<{ type: 'text' | 'number' | 'textlist' | 'numberlist' | 'link' | 'date' }>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);

  form: FormGroup = this.fb.group({
    type: [this.data.type],
    name: [`field_${Math.floor(10000 + Math.random() * 90000)}`],
    label: [''],
    default: [''],
    description: [''],
    url: [''],
    prefix: [''],
    suffix: [''],
  });

  readonly tooltips = {
    type: "text — valeur unique saisie par l'utilisateur.\nnumber — nombre (entier ou décimal) saisi par l'utilisateur, substitué sans guillemets dans le SQL.\ntextlist — liste de valeurs texte sur plusieurs lignes, utilisée dans une clause IN.\nnumberlist — liste de nombres sur plusieurs lignes, utilisée dans une clause IN (sans guillemets).\ndate — sélecteur de date, substitué au format YYYY-MM-DD.\nlink — lien hypertexte calculé par la base de données.",
    url: 'Expression SQL évaluée par la base de données pour construire l\'URL.\n\nExemple avec un identifiant dynamique :\nCONCAT(\'https://user.apps.birdz.com/\', id)\n\nLe tag doit être suivi d\'un alias AS :\n{link url="..." label="..."} AS nom_colonne',
    linkLabel: "Texte affiché dans le tableau à la place de l'URL brute.\nSi vide, la valeur de l'URL est affichée.",
    name: 'Identifiant interne du champ, unique dans la requête.\nLettres, chiffres et _ uniquement.\nRéférencé dans le SQL via {field name="..."}.',
    fieldLabel:
      "Libellé affiché à l'utilisateur lors de l'exécution de la requête.\nSi vide, l'identifiant du champ est utilisé.",
    default: "Valeur pré-remplie dans le formulaire d'exécution.\nL'utilisateur peut la modifier avant d'exécuter.",
    description: "Texte d'aide affiché dans un bouton ? à côté du champ lors de l'exécution.",
    prefix:
      'Texte ajouté avant la valeur saisie par l\'utilisateur dans le SQL.\nEx : prefix="PDS_" avec la saisie "BREST_EP-001" → \'PDS_BREST_EP-001\'',
    suffix:
      'Texte ajouté après la valeur saisie par l\'utilisateur dans le SQL.\nEx : suffix="_FR" avec la saisie "BREST_EP-001" → \'BREST_EP-001_FR\'',
  };

  get isLink(): boolean {
    return this.form.get('type')!.value === 'link';
  }

  get isText(): boolean {
    return this.form.get('type')!.value === 'text';
  }

  ngOnInit(): void {
    this.updateValidators(this.data.type);
    this.form.get('type')!.valueChanges.subscribe((type) => this.updateValidators(type));
  }

  private updateValidators(type: string): void {
    const nameCtrl = this.form.get('name')!;
    const urlCtrl = this.form.get('url')!;
    if (type === 'link') {
      nameCtrl.clearValidators();
      urlCtrl.setValidators([Validators.required]);
    } else {
      nameCtrl.setValidators([Validators.required, Validators.pattern(/^[a-zA-Z0-9_]+$/)]);
      urlCtrl.clearValidators();
    }
    nameCtrl.updateValueAndValidity();
    urlCtrl.updateValueAndValidity();
  }

  confirm(): void {
    if (this.form.invalid) {
      return;
    }
    const { type, name, label, default: def, description, url, prefix, suffix } = this.form.value;
    const tag =
      type === 'link'
        ? buildLinkTag({ url, label })
        : buildFieldTag({ type, name, label, default: def, description, prefix, suffix });
    this.dialogRef.close(tag);
  }
}
